import React, { useCallback, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { LucideIconName } from 'utils/lucideIcon'
import { BaseRouteParams, useNavigation } from 'hocs'
import { LoadDataParams, ReactTable, ReactTableRows, TableColumn } from 'uiComponents/table/reactTable'
import { DiscountPool } from 'discounts/types'
import { useLazyListDiscountPoolsQuery } from 'discounts/reduxQuery'
import { PoolUploadsTable } from './poolUploadsTable'
import { formatTableState } from 'uiComponents/table/reactTable/tableState'
import { Text } from 'uiComponents/typography'
import { InlineNameEdit } from './inlineNameEdit'
import { addSeparators } from 'utils'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { setHash } from 'utils/hash'
import classNames from 'classnames'
import { Chevron } from 'uiComponents/chevron'
import './poolsTable.scss'

const emptyArray = [] as any[]

export const PoolsTable: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [fetchDiscountPools, { data, isFetching }] = useLazyListDiscountPoolsQuery()
    const { accountSlug } = useParams<BaseRouteParams>()
    const location = useLocation()
    const navigation = useNavigation()
    const { search, searchBy } = navigation.query()

    const columns = useMemo(() => {
        return [
            {
                Header: () => null,
                accessor: 'expander',
                className: 'expander',
                width: '16px',
                disableSortBy: true,
                disableHideColumn: true,
                Cell: ({ row }) => {
                    if (!row.original.codes?.length && !row.original.uploads?.length) {
                        return null
                    }

                    if (search === row.original.name) {
                        row.isExpanded = true
                    }

                    return <Chevron direction={row.isExpanded ? 'up' : 'down'} />
                },
            },
            {
                accessor: 'name',
                Header: 'Pool name',
                className: 'name',
                Cell: ({ row: { original } }) => {
                    return <InlineNameEdit discountPool={original} />
                },
            },
            {
                accessor: 'usage',
                Header: 'Count',
                className: 'usage',
                align: 'right',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const result = `${addSeparators(original.usage.used)}/${addSeparators(original.usage.total)}`
                    const isAllResourcesUsed = original.usage.used === original.usage.total
                    const isAllResourcesGettingLow =
                        original.usage.used &&
                        original.usage.total &&
                        (original.usage.used * 100) / original.usage.total >= 90

                    if (isAllResourcesUsed) {
                        return <Text status="error">{result}</Text>
                    }

                    if (isAllResourcesGettingLow) {
                        return <Text status="processing">{result}</Text>
                    }

                    return <Text status="success">{result}</Text>
                },
            },
            {
                accessor: 'action',
                disableHideColumn: true,
                disableSortBy: true,
                align: 'right',
                width: '4.5rem',
                style: {
                    overflow: 'visible',
                },
                Cell: ({
                    row: {
                        original: { uuid, usage },
                    },
                }) => {
                    const options = [
                        {
                            label: 'Add discount code',
                            icon: 'Tag' as LucideIconName,
                            onClick: () => setHash('add_discount', { poolUuid: uuid }),
                        },
                        {
                            label: 'Rename pool',
                            icon: 'Pencil' as LucideIconName,
                            onClick: () => setHash('edit_pool_name', { poolUuid: uuid }),
                        },
                    ]

                    if (usage.used === usage.total) {
                        options.push({
                            label: 'Delete pool',
                            icon: 'Trash' as LucideIconName,
                            onClick: () => setHash('delete_pool', { poolUuid: uuid }),
                        })
                    }

                    return <KebabMenu options={options} listAnchor="right" />
                },
            },
        ] as TableColumn<DiscountPool>[]
    }, [location])

    const loadData = useCallback(
        (params: LoadDataParams) => {
            fetchDiscountPools({
                accountSlug,
                query: formatTableState.toQueryFromLoadData(params, {
                    include: 'uploads,codes',
                    search,
                    search_by: searchBy,
                }),
            })
        },
        [accountSlug, search, searchBy],
    )

    return (
        <>
            <ReactTable<DiscountPool>
                id="discount-pools-table"
                noResultsRow
                pagination
                sort={{ prop: 'name', direction: 'asc' }}
                expanded={PoolUploadsTable}
                loading={isFetching}
                elevation={false}
                rowProps={(row: ReactTableRows<DiscountPool>) => {
                    const hasChildren = row.original.codes?.length || row.original.uploads?.length
                    return {
                        className: classNames('pool row', { expandable: hasChildren }),
                        onClick: () => {
                            if (hasChildren) {
                                row.getToggleRowExpandedProps().onClick()
                            }
                        },
                    }
                }}
                data={data?.entries || emptyArray}
                totalCount={data?.totalCount}
                columns={columns}
                loadData={loadData}
                backgroundColor="var(--white)"
            />
            {children}
        </>
    )
}
